import { useMemo, type ReactNode } from "react";
import { CardListGroup } from "./card-list-group.content";

interface CardListProps<T> {
  items: T[];
  /** Returns the key used for grouping items (displayed as group label) */
  getGroupingKeyAndLabel: (item: T) => string;
  /** Returns a sortable date value (ISO string or Date) for sorting groups and items */
  getSortDate: (item: T) => string | Date;
  getKey: (item: T) => string;
  renderItem: (item: T) => ReactNode;
  /** Sort order for items and groups. Defaults to "desc" (newest first) */
  sortOrder?: "asc" | "desc";
}

export function CardList<T>({ items, getGroupingKeyAndLabel, getKey, renderItem, getSortDate, sortOrder = "desc" }: CardListProps<T>) {
  const groupedItems = useMemo(() => {
    const multiplier = sortOrder === "asc" ? 1 : -1;

    // Sort items by date
    const sortedItems = [...items].sort((a, b) => {
      const dateA = new Date(getSortDate(a)).getTime();
      const dateB = new Date(getSortDate(b)).getTime();
      return (dateA - dateB) * multiplier;
    });

    // Group by display date
    const groups = new Map<string, { items: T[]; sortDate: Date }>();
    sortedItems.forEach(item => {
      const groupingKey = getGroupingKeyAndLabel(item);
      if (!groups.has(groupingKey)) {
        groups.set(groupingKey, { items: [], sortDate: new Date(getSortDate(item)) });
      }
      groups.get(groupingKey)!.items.push(item);
    });

    // Sort groups by date
    const sortedEntries = Array.from(groups.entries()).sort(([, a], [, b]) => (a.sortDate.getTime() - b.sortDate.getTime()) * multiplier);
    return new Map(sortedEntries);
  }, [items, getGroupingKeyAndLabel, getSortDate, sortOrder]);

  return (
    <div className="w-full space-y-4">
      {Array.from(groupedItems.entries()).map(([date, { items: groupItems }]) => (
        <CardListGroup key={date} date={date}>
          <div className="space-y-2">
            {groupItems.map(item => (
              <div key={getKey(item)}>{renderItem(item)}</div>
            ))}
          </div>
        </CardListGroup>
      ))}
    </div>
  );
}
